// Copyright 2019 Sorama B.V.

import { BindingEngine, Disposable, TaskQueue, bindable, containerless, customElement } from "aurelia-framework";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { AspectItemsCatalog } from "./AspectItemsCatalog";
import { AspectItemRatio, IAspectItem } from "./IAspectItem";

export class DivAspectEvent {
    public externalHeight: number;
    public externalWidth: number;
}

@customElement("div-aspect")
@containerless
export class DivAspect implements IAspectItem {
    private resizeDisposable: Disposable;
    private propertyObserverDisposable: Disposable;
    private aspectItemDisposable: Disposable;
    private padding_left = 0;
    private padding_right = 0;
    private max_height: string;

    private wrapper: HTMLElement;

    @bindable
    public ratio: number;

    @bindable
    public changedCallback: (event: DivAspectEvent) => void;

    @bindable
    public maxHeight: string;

    @bindable
    public background: string;

    constructor(
        private readonly resizeListener: ResizeListener,
        private readonly bindingEngine: BindingEngine,
        private aspectItemContainer: AspectItemsCatalog,
        private taskQueue: TaskQueue
    ) {}

    public attached(): void {
        const resizeTask = () => {
            if (this.resizeDisposable) {
                this.resizeDisposable.dispose();
                this.resizeDisposable = undefined;
            }
            if (this.aspectItemDisposable) {
                this.aspectItemDisposable.dispose();
                this.aspectItemDisposable = undefined;
            }

            if (this.wrapper) {
                this.resizeDisposable = this.resizeListener.elementResizeEvent(this.wrapper, () => this.sizeChanged());
                this.aspectItemDisposable = this.aspectItemContainer.subscribe({
                    element: this.wrapper,
                    viewModel: this,
                });
            }

            this.sizeChanged();
        };

        this.bindingEngine.propertyObserver(this, "wrapper").subscribe(resizeTask);

        resizeTask();
    }

    public detached(): void {
        if (this.resizeDisposable) this.resizeDisposable.dispose();

        if (this.propertyObserverDisposable) this.propertyObserverDisposable.dispose();

        if (this.aspectItemDisposable) this.aspectItemDisposable.dispose();
    }

    public getArea(maxWidth: number, maxHeight: number): AspectItemRatio {
        const height = Math.min(maxHeight, maxWidth / this.ratio);
        const width = Math.min(maxWidth, maxHeight * this.ratio);

        return <AspectItemRatio>{
            ratio: this.ratio,
            height: height,
            width: width,
        };
    }

    private ratioChanged(): void {
        this.sizeChanged();
    }

    private sizeChanged(): void {
        if (!this.wrapper) return;

        const totalPadding = Math.max(0, this.wrapper.clientWidth - this.wrapper.clientHeight * this.ratio);
        this.padding_right = Math.ceil(totalPadding / 2);
        this.padding_left = Math.ceil(totalPadding - this.padding_right);
        this.max_height = this.maxHeight ? this.maxHeight : this.wrapper.clientWidth / this.ratio + "px";

        if (this.changedCallback) {
            this.taskQueue.queueTask(() => {
                this.changedCallback({
                    externalWidth: this.wrapper.clientWidth,
                    externalHeight: this.wrapper.clientHeight,
                });
            });
        }
    }
}
